/**
 * usePublicDetailZoom
 *
 * Flies the public map to the currently-open artifact. The active artifact is
 * URL-driven (`descriptor`), so a single effect keyed on its resolved location
 * covers every entry point uniformly: opening a related task/note tab,
 * switching tabs, and closing a tab (which re-activates the previous one).
 *
 * Coordinates are resolved through the shared `resolveDescriptorCoordinates`
 * (the same helper `usePublicSharePage` uses for list/map clicks), so all three
 * artifact kinds — task, note, obstruction — are covered. An artifact with no
 * location resolves to `null`, so the map stays put.
 *
 * The effect keys on the resolved lat/lng rather than the raw map datasets, so
 * a background refresh (e.g. a viewer filter change) that leaves the open
 * artifact in place doesn't yank the camera; the dataset matching the open
 * artifact stays subscribed so a deep link flies into view once its coords hydrate.
 *
 * Mirrors the authenticated `useTabAwareArtifactNavigation` zoom, which the
 * public navigation hook (`usePublicDetailNavigation`) deliberately omits. The
 * store-driven `zoomTo` is already wired to the public map shell via
 * `useZoomToCoordinatesEffect` (see `usePublicMapShellState`).
 *
 * @module components/share/public/usePublicDetailZoom
 */
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useDeviceLayout } from '@/hooks';
import { useGeoMarkupStore } from '@/store';
import { selectMapNotes } from '@/store/selectors';
import { selectMapTasks } from '@/store/selectors/taskSelectors';
import { selectObstructionsMapItems } from '@/store/selectors/obstructionSelectors';
import { previewZoomBottomPadding } from '@/utils/zoomHelpers';

import { resolveDescriptorCoordinates } from './usePublicSharePage';
import type { PublicArtifactDescriptor } from './usePublicArtifactSelection';

const NO_TASKS: ReturnType<typeof selectMapTasks> = [];
const NO_NOTES: ReturnType<typeof selectMapNotes> = [];
const NO_OBSTRUCTIONS: ReturnType<typeof selectObstructionsMapItems> = [];

export function usePublicDetailZoom(
  descriptor: PublicArtifactDescriptor | null
): void {
  const zoomTo = useGeoMarkupStore((state) => state.map.zoomTo);
  const { isMobile } = useDeviceLayout();

  // Resolve coordinates inside the selector, reading ONLY the MAP-tier dataset
  // matching the open artifact's kind (the shared resolver switches on `kind`
  // and ignores the rest, so empties are safe). This keeps a refresh of an
  // unrelated dataset from re-rendering the panel, while the relevant dataset
  // stays subscribed so a deep-linked artifact flies in once its coords hydrate.
  const coordinates = useGeoMarkupStore(
    useShallow((state) =>
      descriptor
        ? resolveDescriptorCoordinates(
            descriptor,
            descriptor.kind === 'task' ? selectMapTasks(state) : NO_TASKS,
            descriptor.kind === 'note' ? selectMapNotes(state) : NO_NOTES,
            descriptor.kind === 'obstruction'
              ? selectObstructionsMapItems(state)
              : NO_OBSTRUCTIONS
          )
        : null
    )
  );

  // Depend on the primitive lat/lng so an unrelated dataset refresh that keeps
  // the open artifact in the same spot doesn't re-trigger a camera flight.
  const lat = coordinates?.lat ?? null;
  const lng = coordinates?.lng ?? null;

  useEffect(() => {
    if (lat !== null && lng !== null) {
      // Mirror the list-click path: frame the pin above the mobile preview
      // sheet so it isn't hidden behind it (no-op padding on desktop).
      zoomTo(
        { lat, lng },
        { bottomPadding: previewZoomBottomPadding(isMobile) }
      );
    }
  }, [lat, lng, isMobile, zoomTo]);
}
